import React from 'react';
import { AlertTriangle, X, User, Calendar, Briefcase, Trash2 } from 'lucide-react';

const ConflictsModal = ({ isOpen, onClose, conflicts, users, projects, onDeleteAssignment }) => {
  if (!isOpen) return null;

  const handleDeleteAssignment = async (assignment) => {
    const project = projects.find(p => p.id === assignment.projectId);
    const user = users.find(u => u.id === assignment.userId);
    
    const confirmMessage = `Are you sure you want to delete this assignment?\n\n` +
      `Person: ${user?.first_name} ${user?.last_name}\n` +
      `Project: ${project?.name}\n` +
      `Date: ${formatDate(assignment.startDate)} - ${formatDate(assignment.endDate)}\n` +
      `Role: ${assignment.role}`;
      
    if (window.confirm(confirmMessage)) {
      await onDeleteAssignment(assignment.id);
    }
  };

  const formatDate = (date) => {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProjectName = (projectId, conflict = null, assignment = null) => {
    // If this is a training assignment, use training name
    if (assignment?.isTraining || projectId?.toString().startsWith('training-')) {
      if (conflict && conflict.trainingName) {
        return conflict.trainingName;
      }
      return 'Training';
    }

    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
  };

  const getProjectColor = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.color || '#6B7280';
  };

  const calculateOverlapDays = (assignment1, assignment2) => {
    const start1 = new Date(assignment1.startDate);
    const end1 = new Date(assignment1.endDate);
    const start2 = new Date(assignment2.startDate);
    const end2 = new Date(assignment2.endDate);

    const overlapStart = new Date(Math.max(start1.getTime(), start2.getTime()));
    const overlapEnd = new Date(Math.min(end1.getTime(), end2.getTime()));
    
    if (overlapStart <= overlapEnd) {
      const diffTime = Math.abs(overlapEnd - overlapStart);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-steel-200">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center">
            <AlertTriangle className="h-6 w-6 mr-2 text-red-500" />
            Assignment Conflicts ({conflicts.length})
          </h2>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {conflicts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-green-600 text-lg font-medium mb-2">No Conflicts Found!</div>
              <div className="text-slate-600">All assignments are properly scheduled without overlaps.</div>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4 text-sm text-slate-600">
                Found {conflicts.length} conflicts where people have overlapping project assignments.
              </div>
              
              <div className="space-y-6">
                {conflicts.map((conflict, index) => (
                  <div key={index} className="border border-red-200 rounded-xl p-4 bg-red-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-red-600 mr-2" />
                        <h3 className="text-lg font-semibold text-red-800">
                          {getUserName(conflict.userId)}
                        </h3>
                      </div>
                      <div className="text-sm text-red-600 font-medium">
                        {calculateOverlapDays(conflict.assignment1, conflict.assignment2)} day(s) overlap
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* First Assignment */}
                      <div className="bg-white rounded-xl p-4 border border-steel-200 relative group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: getProjectColor(conflict.assignment1.projectId) }}
                            ></div>
                            <div className="font-medium text-slate-900">
                              {getProjectName(conflict.assignment1.projectId, conflict, conflict.assignment1)}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAssignment(conflict.assignment1)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                            title="Delete this assignment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="space-y-1 text-sm text-slate-600">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(conflict.assignment1.startDate)} - {formatDate(conflict.assignment1.endDate)}
                          </div>
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-2" />
                            Role: {conflict.assignment1.role}
                          </div>
                        </div>
                      </div>

                      {/* Second Assignment */}
                      <div className="bg-white rounded-xl p-4 border border-steel-200 relative group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: getProjectColor(conflict.assignment2.projectId) }}
                            ></div>
                            <div className="font-medium text-slate-900">
                              {getProjectName(conflict.assignment2.projectId, conflict, conflict.assignment2)}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAssignment(conflict.assignment2)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                            title="Delete this assignment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="space-y-1 text-sm text-slate-600">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(conflict.assignment2.startDate)} - {formatDate(conflict.assignment2.endDate)}
                          </div>
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-2" />
                            Role: {conflict.assignment2.role}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Overlap Details */}
                    <div className="mt-3 p-3 bg-red-100 rounded-xl">
                      <div className="text-sm">
                        <div className="font-medium text-red-800 mb-1">Conflict Details:</div>
                        <div className="text-red-700">
                          These assignments overlap from{' '}
                          <span className="font-medium">
                            {formatDate(new Date(Math.max(
                              new Date(conflict.assignment1.startDate).getTime(),
                              new Date(conflict.assignment2.startDate).getTime()
                            )))}
                          </span>
                          {' '}to{' '}
                          <span className="font-medium">
                            {formatDate(new Date(Math.min(
                              new Date(conflict.assignment1.endDate).getTime(),
                              new Date(conflict.assignment2.endDate).getTime()
                            )))}
                          </span>
                          {' '}({calculateOverlapDays(conflict.assignment1, conflict.assignment2)} days)
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5" />
                  <div>
                    <div className="font-medium text-amber-800 mb-1">Resolution Suggestions:</div>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Adjust assignment dates to avoid overlaps</li>
                      <li>• Consider splitting assignments between multiple team members</li>
                      <li>• Review project priorities and deadlines</li>
                      <li>• Use drag-and-drop to reschedule conflicting assignments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-steel-200 p-4 bg-steel-50">
          <div className="flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-steel-600 text-white rounded-xl hover:bg-steel-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConflictsModal;